import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { User, UserDoseHistory, Order, DosePurchaseEligibility } from '../../interfaces/product.interface';

@Component({
  selector: 'app-profile',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './profile.html',
  styleUrl: './profile.scss'
})
export class Profile implements OnInit {
  currentUser: User | null = null;
  profileForm: FormGroup;
  doseEligibility: DosePurchaseEligibility | null = null;
  userOrders: Order[] = [];
  doseHistory: UserDoseHistory[] = [];
  isEditing = false;
  isLoading = false;
  successMessage = '';
  errorMessage = '';
  activeTab = 'overview'; // overview, profile, doses, orders

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private orderService: OrderService,
    private router: Router
  ) {
    this.profileForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      currentWeight: ['', [Validators.required, Validators.min(20), Validators.max(200)]],
      currentHeight: ['', [Validators.required, Validators.min(100), Validators.max(250)]],
      fullAddress: ['', [Validators.required, Validators.minLength(10)]],
      altMobile: ['', [Validators.pattern(/^[6-9]\d{9}$/)]]
    });
  }

  ngOnInit(): void {
    // Subscribe to authentication state and load user data
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) {
        this.loadUserData();
      } else {
        // User is not logged in, redirect to login
        this.router.navigate(['/login']);
      }
    });
  }

  loadUserData(): void {
    if (!this.currentUser) return;

    // Populate form with current user data
    this.profileForm.patchValue({
      name: this.currentUser.name,
      email: this.currentUser.email,
      currentWeight: this.currentUser.currentWeight,
      currentHeight: this.currentUser.currentHeight,
      fullAddress: this.currentUser.fullAddress,
      altMobile: this.currentUser.altMobile || ''
    });

    // Load dose eligibility
    this.authService.checkDosePurchaseEligibility(this.currentUser.id).subscribe(eligibility => {
      this.doseEligibility = eligibility;
    });

    // Load user orders
    this.orderService.getUserOrders(this.currentUser.id).subscribe(orders => {
      this.userOrders = orders;
    });

    // Load dose history
    this.doseHistory = this.currentUser.currentDoseHistory || [];
  }

  // Update profile
  updateProfile(): void {
    if (this.profileForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const updatedData = this.profileForm.value;
      this.authService.updateUserProfile(updatedData).subscribe(response => {
        this.isLoading = false;
        if (response.success) {
          this.successMessage = response.message;
          this.isEditing = false;
          setTimeout(() => this.successMessage = '', 3000);
        } else {
          this.errorMessage = response.message;
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  // Toggle edit mode
  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    this.errorMessage = '';
    this.successMessage = '';
    
    if (!this.isEditing) {
      // Reset form to original values
      this.loadUserData();
    }
  }

  // Switch tabs
  switchTab(tab: string): void {
    this.activeTab = tab;
  }

  // Calculate BMI
  calculateBMI(): number {
    if (!this.currentUser) return 0;
    const height = this.currentUser.currentHeight / 100; // Convert cm to meters
    return this.currentUser.currentWeight / (height * height);
  }

  // Get BMI category
  getBMICategory(): string {
    const bmi = this.calculateBMI();
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal weight';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  }

  // Get dose progress percentage
  getDoseProgressPercentage(): number {
    if (!this.doseEligibility) return 0;
    const completedCount = this.doseEligibility.completedDoses.length;
    return (completedCount / 3) * 100;
  }

  // Get next dose to purchase
  getNextDose(): string {
    if (!this.doseEligibility) return 'Loading...';
    
    if (this.doseEligibility.canPurchaseDose1) return 'Green Dose (Foundation)';
    if (this.doseEligibility.canPurchaseDose2) return 'Yellow Dose (Enhancement)';
    if (this.doseEligibility.canPurchaseDose3) return 'Red Dose (Maximum)';
    
    return 'Complete treatment!';
  }

  // Get days remaining for active dose
  getActiveDoseDaysRemaining(): number {
    return this.doseEligibility?.currentActiveDose?.daysRemaining || 0;
  }

  // Format date
  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-IN');
  }

  // Get order status color
  getOrderStatusColor(status: string): string {
    switch (status) {
      case 'pending': return '#FFA726';
      case 'confirmed': return '#42A5F5';
      case 'shipped': return '#AB47BC';
      case 'delivered': return '#66BB6A';
      case 'cancelled': return '#EF5350';
      default: return '#9E9E9E';
    }
  }

  // Get dose status color
  getDoseStatusColor(status: string): string {
    switch (status) {
      case 'not_started': return '#9E9E9E';
      case 'in_progress': return '#FFA726';
      case 'completed': return '#66BB6A';
      case 'expired': return '#EF5350';
      default: return '#9E9E9E';
    }
  }

  // Navigate to products
  navigateToProducts(): void {
    this.router.navigate(['/products']);
  }

  // Navigate to specific order
  viewOrder(orderId: string): void {
    // TODO: Implement order detail view
    console.log('View order:', orderId);
  }

  // Logout
  logout(): void {
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/login']);
    });
  }

  // Mark form as touched for validation
  private markFormGroupTouched(): void {
    Object.keys(this.profileForm.controls).forEach(key => {
      const control = this.profileForm.get(key);
      control?.markAsTouched();
    });
  }

  // Getter methods for form controls
  get name() { return this.profileForm.get('name'); }
  get email() { return this.profileForm.get('email'); }
  get currentWeight() { return this.profileForm.get('currentWeight'); }
  get currentHeight() { return this.profileForm.get('currentHeight'); }
  get fullAddress() { return this.profileForm.get('fullAddress'); }
  get altMobile() { return this.profileForm.get('altMobile'); }
}
